/**
 * Photos tab — UX/UI Specification §9.8.
 *
 * Photos are optional everywhere in this product. A student may have one, or
 * only regional ones, or none at all, and nothing here nags about it.
 */

import { useMemo, useState } from 'react';
import { formatDateLong, PHOTO_CATEGORY_LABELS, type PhotoCategory } from '@pp/shared';
import { ApiError, request } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';
import { AdminReauthDialog } from '../components/ui/AdminReauthDialog.js';
import { Button } from '../components/ui/Button.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { useApiData } from '../hooks/useApiData.js';
import { ImageError, optimizeImage } from '../lib/image.js';
import { useSession } from '../session/SessionContext.js';
import './PhotosTab.css';

interface Photo {
  id: string;
  capturedAt: string;
  category: PhotoCategory;
  customLabel: string | null;
  url: string;
  widthPx: number | null;
  heightPx: number | null;
}

const CATEGORIES: PhotoCategory[] = [
  'front',
  'side',
  'back',
  'waist',
  'hip',
  'leg',
  'arm',
  'custom',
];

function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function labelFor(photo: Photo): string {
  return photo.category === 'custom' && photo.customLabel
    ? photo.customLabel
    : PHOTO_CATEGORY_LABELS[photo.category];
}

export function PhotosTab({ studentId }: { studentId: string }) {
  const { data, error, loading, reload } = useApiData<{ items: Photo[] }>(
    `/api/students/${studentId}/photos`,
  );
  const { hasAdminAccess } = useSession();

  const [filter, setFilter] = useState<PhotoCategory | 'all'>('all');
  const [adding, setAdding] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [capturedAt, setCapturedAt] = useState(todayIso());
  const [category, setCategory] = useState<PhotoCategory>('front');
  const [customLabel, setCustomLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string>();

  const [compare, setCompare] = useState<Photo[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<Photo | null>(null);
  const [reauthFor, setReauthFor] = useState<(() => void) | null>(null);

  const items = data?.items ?? [];
  const shown = useMemo(
    () => (filter === 'all' ? items : items.filter((p) => p.category === filter)),
    [items, filter],
  );

  function resetForm() {
    setFile(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setCustomLabel('');
    setCapturedAt(todayIso());
    setFormError(undefined);
  }

  function chooseFile(chosen: File | undefined) {
    if (!chosen) return;
    setFormError(undefined);
    setFile(chosen);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(chosen));
  }

  async function upload(event: React.FormEvent) {
    event.preventDefault();
    if (!file) return;

    setBusy(true);
    setFormError(undefined);

    try {
      const optimized = await optimizeImage(file);

      const form = new FormData();
      form.append('image', optimized.file);
      form.append('capturedAt', capturedAt);
      form.append('category', category);
      form.append('widthPx', String(optimized.width));
      form.append('heightPx', String(optimized.height));
      if (category === 'custom') form.append('customLabel', customLabel);

      await request(`/api/students/${studentId}/photos`, {
        method: 'POST',
        formData: form,
      });

      resetForm();
      setAdding(false);
      reload();
    } catch (err) {
      // Everything the user chose stays on screen, including the selected
      // file: a failed upload must not cost them the photo they picked
      // (PRD §18).
      if (err instanceof ImageError) setFormError(messageFor(err.code));
      else if (err instanceof ApiError) setFormError(messageFor(err.code));
      else setFormError(messageFor('INTERNAL_ERROR'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Deleting a photo removes the stored image too, so it asks first. There is
   * no undo to offer once the object is gone.
   */
  async function remove(photo: Photo) {
    try {
      await request(`/api/students/${studentId}/photos/${photo.id}`, {
        method: 'DELETE',
      });
      setCompare((current) => current.filter((p) => p.id !== photo.id));
      reload();
    } catch (err) {
      setFormError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    }
  }

  /** Runs now if the admin window is open, otherwise buys ten minutes first. */
  function withAdmin(action: () => void) {
    if (hasAdminAccess()) action();
    else setReauthFor(() => action);
  }

  function toggleCompare(photo: Photo) {
    setCompare((current) => {
      if (current.some((p) => p.id === photo.id)) {
        return current.filter((p) => p.id !== photo.id);
      }
      // Two at a time — a comparison is between two dates.
      return [...current.slice(-1), photo];
    });
  }

  const comparePair =
    compare.length === 2
      ? [...compare].sort((a, b) => (a.capturedAt < b.capturedAt ? -1 : 1))
      : null;

  return (
    <div className="photos">
      <div className="photos__head">
        <h2 className="profile__section">Gelişim Fotoğrafları</h2>
        {!adding && (
          <Button type="button" onClick={() => setAdding(true)}>
            Fotoğraf Yükle
          </Button>
        )}
      </div>

      {error && <p role="alert">{error}</p>}

      {adding && (
        <form onSubmit={upload} className="photos__form" noValidate>
          <div className="photos__form-row">
            <label className="photos__field">
              <span>Fotoğraf</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => chooseFile(e.target.files?.[0])}
                required
              />
            </label>

            <label className="photos__field">
              <span>Çekim Tarihi</span>
              <input
                type="date"
                value={capturedAt}
                max={todayIso()}
                onChange={(e) => setCapturedAt(e.target.value)}
                required
              />
            </label>
          </div>

          <fieldset className="photos__categories">
            <legend className="photos__legend">Bölge</legend>
            <div className="photos__chips">
              {CATEGORIES.map((value) => (
                <label
                  key={value}
                  className={category === value ? 'chip chip--on' : 'chip'}
                >
                  <input
                    type="radio"
                    name="category"
                    value={value}
                    checked={category === value}
                    onChange={() => setCategory(value)}
                    className="pp-visually-hidden"
                  />
                  {PHOTO_CATEGORY_LABELS[value]}
                </label>
              ))}
            </div>
          </fieldset>

          {category === 'custom' && (
            <label className="photos__field">
              <span>Bölge adı</span>
              <input
                type="text"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder="Omuz, Sırt, Karın…"
                required
              />
            </label>
          )}

          {preview && (
            <img
              src={preview}
              alt="Seçilen fotoğrafın önizlemesi"
              className="photos__preview"
            />
          )}

          {formError && (
            <p className="photos__error" role="alert">
              {formError}
            </p>
          )}

          <div className="photos__actions">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setAdding(false);
                resetForm();
              }}
            >
              Vazgeç
            </Button>
            <Button type="submit" loading={busy} disabled={!file}>
              {busy ? 'Yükleniyor…' : 'Yükle'}
            </Button>
          </div>
        </form>
      )}

      {loading && <p className="profile__muted">Yükleniyor…</p>}

      {!loading && items.length === 0 && !adding && (
        <div className="profile__empty">
          <p>Henüz gelişim fotoğrafı yüklenmemiş.</p>
          <Button type="button" onClick={() => setAdding(true)}>
            Fotoğraf Yükle
          </Button>
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="photos__filter" role="group" aria-label="Bölge filtresi">
            <button
              type="button"
              className={filter === 'all' ? 'chip chip--on' : 'chip'}
              onClick={() => setFilter('all')}
              aria-pressed={filter === 'all'}
            >
              Tümü
            </button>
            {CATEGORIES.filter((cat) => items.some((p) => p.category === cat)).map(
              (cat) => (
                <button
                  key={cat}
                  type="button"
                  className={filter === cat ? 'chip chip--on' : 'chip'}
                  onClick={() => setFilter(cat)}
                  aria-pressed={filter === cat}
                >
                  {PHOTO_CATEGORY_LABELS[cat]}
                </button>
              ),
            )}
          </div>

          {comparePair && (
            <div className="photos__compare">
              <h3 className="photos__compare-title">Karşılaştırma</h3>
              <div className="photos__compare-pair">
                {comparePair.map((photo) => (
                  <figure key={photo.id} className="photos__compare-item">
                    <img
                      src={photo.url}
                      alt={`${labelFor(photo)}, ${formatDateLong(photo.capturedAt)}`}
                    />
                    <figcaption>
                      {labelFor(photo)} · {formatDateLong(photo.capturedAt)}
                    </figcaption>
                  </figure>
                ))}
              </div>
              <Button type="button" variant="ghost" onClick={() => setCompare([])}>
                Karşılaştırmayı kapat
              </Button>
            </div>
          )}

          <ul className="photos__grid">
            {shown.map((photo) => {
              const selected = compare.some((p) => p.id === photo.id);
              return (
                <li key={photo.id} className="photos__item">
                  <button
                    type="button"
                    className={
                      selected ? 'photos__thumb photos__thumb--on' : 'photos__thumb'
                    }
                    onClick={() => toggleCompare(photo)}
                    aria-pressed={selected}
                  >
                    <img
                      src={photo.url}
                      alt={`${labelFor(photo)}, ${formatDateLong(photo.capturedAt)}`}
                      loading="lazy"
                    />
                  </button>
                  <div className="photos__meta">
                    <span className="photos__label">{labelFor(photo)}</span>
                    {/* A comparison without dates is not evidence of anything. */}
                    <span className="photos__date">
                      {formatDateLong(photo.capturedAt)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="photos__delete"
                    onClick={() => setConfirmDelete(photo)}
                  >
                    Sil
                  </button>
                </li>
              );
            })}
          </ul>

          <p className="photos__hint">Karşılaştırmak için iki fotoğrafa tıklayın.</p>
        </>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Fotoğraf silinsin mi?"
          body={`${labelFor(confirmDelete)} · ${formatDateLong(
            confirmDelete.capturedAt,
          )} kalıcı olarak silinecek. Bu işlem geri alınamaz.`}
          confirmLabel="Sil"
          destructive
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => {
            const photo = confirmDelete;
            setConfirmDelete(null);
            withAdmin(() => void remove(photo));
          }}
        />
      )}

      {reauthFor && (
        <AdminReauthDialog
          action="Fotoğrafı kalıcı olarak silmek"
          onCancel={() => setReauthFor(null)}
          onConfirmed={() => {
            const action = reauthFor;
            setReauthFor(null);
            action();
          }}
        />
      )}
    </div>
  );
}
